"use client";

import {
  notFound,
  useParams,
  useRouter,
  useSearchParams,
} from "next/navigation";

import { ChallengeLockCountdown } from "@/components/tournament/ChallengeLockCountdown";
import { CountdownCard } from "@/components/tournament/CountdownCard";
import { NoticeBanner } from "@/components/tournament/NoticeBanner";
import { TeamListView } from "@/components/tournament/TeamListView";
import { TournamentCountdown } from "@/components/tournament/TournamentCountdown";
import { TowerSkeleton, TowerView } from "@/components/tournament/tower";
import { JoinPoolModal } from "@/components/pool/JoinPoolModal";
import { useAuth } from "@/contexts/AuthContext";
import {
  CombinedTournamentProvider,
  useTeamsContext,
} from "@/contexts/TournamentContext";
import { useTournamentPoolEntries } from "@/hooks/usePool";
import { useUserPoolEntryForTournament } from "@/hooks/useUserPoolEntryForTournament";
import { Link } from "@/i18n/routing";
import { getSportIcon } from "@/lib/sports/icons";
import { Tournament } from "@/types";
import { isJoinOpen } from "@/lib/tournament-phase";
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { differenceInDays, subDays } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslations } from "next-intl";
import { Suspense, useEffect, useState } from "react";
import { JoinTournamentForm } from "../forms/JoinTournamentForm";
import PageLayout from "../layout/PageLayout";

interface TournamentPageClientProps {
  tournament: Tournament;
}

/**
 * Redirect legacy ?chatId=X links (e.g. older push notifications) to the new
 * match chat route. Server-side permission checks happen on the target page.
 */
function ChatIdHandler({ tournamentSlug }: { tournamentSlug: string }) {
  const searchParams = useSearchParams();
  const router = useRouter();

  useEffect(() => {
    const chatId = searchParams.get("chatId");
    if (!chatId) return;
    router.replace(`/tournaments/${tournamentSlug}/chat/${chatId}`);
  }, [searchParams, router, tournamentSlug]);

  return null;
}

function TournamentPageContent({ tournament }: TournamentPageClientProps) {
  const t = useTranslations();
  const router = useRouter();
  const params = useParams();
  const { user } = useAuth();
  const {
    teams: towerTeams,
    currentUserTeam,
    isPending: teamsLoading,
  } = useTeamsContext();
  const { data: poolEntry } = useUserPoolEntryForTournament(tournament.id);
  const isInPool = !!poolEntry;
  const { data: poolEntries } = useTournamentPoolEntries(tournament.id);
  const poolCount = poolEntries?.length ?? 0;
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const [isPoolModalOpen, setIsPoolModalOpen] = useState(false);
  // Prevent hydration mismatch by deferring time-based calculations to client
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Calculate time-based values only after mount to prevent hydration mismatch
  const now = mounted ? new Date() : null;

  const signInStartsAt = tournament.signInStartsAt
    ? new Date(tournament.signInStartsAt)
    : null;
  const signInEndsAt = tournament.signInEndsAt
    ? new Date(tournament.signInEndsAt)
    : null;

  // Tower becomes visible 24 hours before tournament starts, but never before
  // sign-in closes — otherwise pool teams haven't been placed yet and the
  // tower would render empty.
  const defaultTowerOpensAt = new Date(
    new Date(tournament.startsAt).getTime() - 24 * 60 * 60 * 1000,
  );
  const towerOpensAt =
    signInEndsAt && signInEndsAt > defaultTowerOpensAt
      ? signInEndsAt
      : defaultTowerOpensAt;

  // Time-dependent values - only calculated after mount
  const startsAt = new Date(tournament.startsAt);
  const tournamentStarted = now ? now >= startsAt : false;
  const tournamentEnded = now ? now > new Date(tournament.endsAt) : false;
  const endsAt = new Date(tournament.endsAt);
  const challengeLockAt = subDays(endsAt, 7);
  const challengesLocked = now
    ? now >= challengeLockAt && !tournamentEnded
    : false;
  const challengeLockApproaching = now
    ? now >= subDays(challengeLockAt, 7) && now < challengeLockAt
    : false;
  const daysUntilEnd = now ? differenceInDays(endsAt, now) : 0;
  const isTowerOpen = now ? now >= towerOpensAt : false;
  // Even after the tower window opens, fall back to the sign-in team list when
  // no teams have been placed in the tower yet (everyone is still in the pool) —
  // otherwise the tower would render empty.
  const hasTowerTeams = towerTeams.length > 0;
  const isSignInOpen = now
    ? (!signInStartsAt || now >= signInStartsAt) &&
      (!signInEndsAt || now <= signInEndsAt)
    : false;

  // Determine sign-in status for messaging
  const signInNotYetOpen = now && signInStartsAt ? now < signInStartsAt : false;
  const signInHasClosed = now && signInEndsAt ? now > signInEndsAt : false;

  // Joining is open during the sign-in window, or during the active phase when
  // the organizer allows it (see lib/tournament-phase).
  const joinOpen = now ? isJoinOpen(tournament, now) : false;

  const showJoinButton = !currentUserTeam && !isInPool && joinOpen;
  const showSignInStatus = !currentUserTeam && !isInPool && !joinOpen;
  const supportsPool = tournament.maxPlayersPerTeam >= 2;

  const requireAuth = (next: () => void) => {
    if (!user) {
      const locale = params.locale || "en";
      const currentPath = `/${locale}/tournaments/${tournament.slug}`;
      router.push(
        `/${locale}/login?redirect=${encodeURIComponent(currentPath)}`,
      );
    } else {
      next();
    }
  };

  const handleJoinTeamClick = () => requireAuth(() => setIsJoinModalOpen(true));
  const handleFindTeammatesClick = () =>
    requireAuth(() => setIsPoolModalOpen(true));

  // Generate sign-in status message for header (only after mount)
  const getSignInStatusMessage = () => {
    if (!mounted) return null;
    if (signInNotYetOpen && signInStartsAt) {
      return t("tournaments.join.signInNotOpen", {
        date: formatInTimeZone(signInStartsAt, "Europe/Helsinki", "dd.MM.yyyy HH:mm"),
      });
    }
    if (signInHasClosed && signInEndsAt) {
      return t("tournaments.join.signInClosed", {
        date: formatInTimeZone(signInEndsAt, "Europe/Helsinki", "dd.MM.yyyy HH:mm"),
      });
    }
    return null;
  };

  const signInStatusMessage = showSignInStatus
    ? getSignInStatusMessage()
    : null;

  // Build status labels for header - only show when tower is open and mounted
  const statusLabels: Array<{ icon: string; label: string; color?: string }> =
    [];

  // Sign-in not yet open (show in header as it's important info)
  if (mounted && signInNotYetOpen && signInStartsAt) {
    statusLabels.push({
      icon: "solar:clock-circle-bold",
      label: t("tournaments.join.signInNotOpen", {
        date: formatInTimeZone(signInStartsAt, "Europe/Helsinki", "dd.MM.yyyy HH:mm"),
      }),
      color: "amber",
    });
  }

  const headerActions = showJoinButton
    ? [
        <div
          key="join-actions"
          className="flex w-full max-w-md flex-col items-center gap-3"
        >
          {/* Joined segmented control: filled primary = main action,
              bordered = secondary, visually unified as one element while
              keeping the create/find hierarchy. */}
          <ButtonGroup size="md" className="w-full sm:w-auto">
            <Button
              color="primary"
              onPress={handleJoinTeamClick}
              startContent={
                <Icon icon="solar:users-group-rounded-bold" width={18} />
              }
              className="h-11 flex-1 font-semibold sm:h-12 sm:text-medium"
            >
              {t("tournaments.join.haveTeamButton")}
            </Button>
            {supportsPool ? (
              <Button
                color="primary"
                variant="bordered"
                onPress={handleFindTeammatesClick}
                startContent={<Icon icon="solar:hand-shake-bold" width={18} />}
                className="h-11 flex-1 sm:h-12 sm:text-medium"
              >
                {t("tournaments.join.lookingButton")}
              </Button>
            ) : null}
          </ButtonGroup>
          {/* Explain where a joining team lands: before kickoff (sign-in window
              or the gap before the tournament starts) the organizer seeds them
              from the pool; once the tournament is running they go to the
              bottom of the tower. */}
          <p className="flex items-start justify-center gap-1.5 text-center text-xs leading-relaxed text-white/60">
            <Icon
              icon="solar:info-circle-linear"
              width={14}
              className="mt-0.5 shrink-0 text-white/40"
            />
            <span>
              {tournamentStarted
                ? t("tournaments.join.activePlacementNote")
                : t("tournaments.join.signInPlacementNote")}
            </span>
          </p>
        </div>,
      ]
    : undefined;

  // Format dates only after mount to prevent hydration mismatch
  const pretitle = mounted
    ? formatInTimeZone(tournament.startsAt, "Europe/Helsinki", "dd.MM. HH:mm") +
      " - " +
      formatInTimeZone(tournament.endsAt, "Europe/Helsinki", "dd.MM.yyyy HH:mm")
    : "";

  return (
    <PageLayout
      className="max-w-8xl"
      pageHeaderProps={{
        title: tournament.name,
        sportIcon: getSportIcon(tournament.sport),
        sportLabel: t(`sports.${tournament.sport}`),
        pretitle,
        statusLabels: statusLabels.length > 0 ? statusLabels : undefined,
        actions: headerActions,
      }}
    >
      {/* Handle chatId query parameter in Suspense boundary */}
      <Suspense fallback={null}>
        <ChatIdHandler tournamentSlug={tournament.slug} />
      </Suspense>

      {supportsPool && poolCount > 0 && (
        <div className="-mt-6 mb-6 flex flex-col items-center gap-3">
          <div className="flex items-center justify-center gap-2 text-sm text-purple-300/80">
            <Icon
              icon="solar:hand-shake-bold"
              className="h-4 w-4 text-purple-400"
            />
            <span>
              {t("tournaments.pool.lookingForTeamCount", {
                count: poolCount,
              })}
            </span>
          </div>
          <Button
            as={Link}
            href={`/tournaments/${tournament.slug}/pool`}
            size="sm"
            variant="bordered"
            className="border-purple-400/40 text-purple-200 hover:bg-purple-500/10"
            startContent={
              <Icon icon="solar:users-group-rounded-bold" width={18} />
            }
          >
            {t("tournaments.pool.viewPlayersButton")}
          </Button>
        </div>
      )}

      {challengeLockApproaching && isTowerOpen && (
        <ChallengeLockCountdown lockAt={challengeLockAt} />
      )}

      {challengesLocked && (
        <NoticeBanner
          className="mb-6"
          accent="warning"
          icon="solar:lock-keyhole-bold"
          title={t("tournaments.challengesLocked.title")}
          description={t("tournaments.challengesLocked.description", {
            days: daysUntilEnd,
          })}
        />
      )}

      {/* Archived tournaments already show the read-only ArchivedTournamentBanner
          at the top of the layout, so this "ended" notice would be redundant.
          Keep it only for tournaments that have ended but are not yet archived. */}
      {tournamentEnded && !tournament.archived && (
        <NoticeBanner
          className="mb-6"
          accent="gold"
          icon="solar:cup-star-bold"
          title={t("tournaments.tournamentEnded.title")}
          description={
            mounted
              ? t("tournaments.tournamentEnded.description", {
                  date: formatInTimeZone(
                    new Date(tournament.endsAt),
                    "Europe/Helsinki",
                    "dd.MM.yyyy HH:mm",
                  ),
                })
              : undefined
          }
        />
      )}

      {/* Show loading skeleton until mounted to prevent hydration mismatch */}
      {!mounted ? (
        <TowerSkeleton
          teamsPerRow={tournament.towerTeamsPerRow}
          topRowTeamsCount={tournament.towerTopRowTeamCount}
        />
      ) : (
        <>
          {/* Countdown cards - show only when tower is not yet open */}
          {!isTowerOpen && !tournamentEnded && (
            <TournamentCountdown
              towerOpensAt={towerOpensAt}
              tournamentStartsAt={startsAt}
              signInStartsAt={signInStartsAt}
              signInEndsAt={signInEndsAt}
              isSignInOpen={isSignInOpen}
              signInNotYetOpen={signInNotYetOpen}
              allowJoinDuringTournament={
                tournament.allowJoinDuringTournament !== false
              }
            />
          )}

          {isTowerOpen ? (
            <>
              {/* Tower is visible but the tournament has not started yet -
                  count down to kickoff above the tower */}
              {!tournamentStarted && !tournamentEnded && (
                <div className="mx-auto mb-8 w-full max-w-md">
                  <CountdownCard
                    targetDate={startsAt}
                    icon="🏆"
                    title={t("tournaments.countdown.tournamentStarts")}
                    accent="amber"
                  />
                </div>
              )}
              {teamsLoading ? (
                <TowerSkeleton
                  teamsPerRow={tournament.towerTeamsPerRow}
                  topRowTeamsCount={tournament.towerTopRowTeamCount}
                />
              ) : hasTowerTeams ? (
                <TowerView />
              ) : (
                <TeamListView
                  showJoinCard={showJoinButton}
                  onJoinTeamClick={handleJoinTeamClick}
                  onFindTeammatesClick={
                    supportsPool ? handleFindTeammatesClick : undefined
                  }
                />
              )}
            </>
          ) : (
            <TeamListView
              showJoinCard={showJoinButton}
              onJoinTeamClick={handleJoinTeamClick}
              onFindTeammatesClick={
                supportsPool ? handleFindTeammatesClick : undefined
              }
            />
          )}
        </>
      )}

      {showJoinButton && (
        <>
          <Modal
            isOpen={isJoinModalOpen}
            onOpenChange={setIsJoinModalOpen}
            size="md"
            placement="top"
            scrollBehavior="inside"
            classNames={{
              base: "m-2 sm:m-4",
              wrapper: "items-start pt-[10vh] sm:items-center sm:pt-0",
              backdrop: "bg-black/80",
              body: "py-4",
              header: "border-b border-white/5 pb-4",
              closeButton:
                "top-3 right-3 hover:bg-white/10 active:bg-white/20 text-default-400",
            }}
          >
            <ModalContent className="bg-[#0f1219] border border-amber-500/20">
              {(onClose) => (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    <div className="flex items-center gap-3 justify-center">
                      <div className="h-px w-8 bg-gradient-to-r from-transparent to-amber-500/40" />
                      <h2 className="font-cinzel text-xl font-bold text-amber-400 tracking-wide">
                        {t("tournaments.join.modal.title")}
                      </h2>
                      <div className="h-px w-8 bg-gradient-to-l from-transparent to-amber-500/40" />
                    </div>
                    <p className="text-sm font-normal text-default-400 text-center">
                      {t("tournaments.join.modal.description", {
                        tournament: tournament.name,
                      })}
                    </p>
                  </ModalHeader>
                  <ModalBody className="pb-6">
                    <JoinTournamentForm
                      tournament={tournament}
                      onSuccess={() => {
                        onClose();
                      }}
                      onCancel={onClose}
                    />
                  </ModalBody>
                </>
              )}
            </ModalContent>
          </Modal>

          {supportsPool && (
            <JoinPoolModal
              tournament={tournament}
              isOpen={isPoolModalOpen}
              onOpenChange={setIsPoolModalOpen}
            />
          )}
        </>
      )}
    </PageLayout>
  );
}

export function TournamentPageClient({
  tournament,
}: TournamentPageClientProps) {
  if (!tournament) {
    return notFound();
  }

  return (
    <CombinedTournamentProvider tournamentId={tournament.id}>
      <TournamentPageContent tournament={tournament} />
    </CombinedTournamentProvider>
  );
}
